import { useEffect, useState } from 'react';
import NewSectionTitle from '@components/shared/NewSectionTitle';
import personImage from '@assets/placementpage-images/porttrait.png';
import Image from 'next/image';
import vectorOne from '@assets/placementpage-images/vector-one.svg';
import vectorTwo from '@assets/placementpage-images/vector-two.svg';
import vectorThree from '@assets/placementpage-images/vector-three.svg';
import EllipticalSegmentChart from './EllipticalSegmentChart';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import arcOne from '@assets/placementpage-images/arc-one.svg';
import arcTwo from '@assets/placementpage-images/arc-two.svg';
import arcThree from '@assets/placementpage-images/arc-three.svg';
import arcFour from '@assets/placementpage-images/arc-four.svg';
import ArcOne from './reusable-svg/ArcOne';
import ArcTwo from './reusable-svg/ArcTwo';
import ArcThree from './reusable-svg/ArcThree';
import ArcFours from './reusable-svg/ArcFours';
import Lines from './reusable-svg/Lines';
import LineTwo from './reusable-svg/LineTwo';
import LineThree from './reusable-svg/LineThree';
import LineFour from './reusable-svg/LineFour';

export default function StudentPlacementChart({ placementPage = true, segments, year, subtitle }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useMediaQuery('(max-width: 767.98px)');
  const segmentsMobileView = [
    { percentage: 2, label: '< 3.5 LPA', position: 'bottom-left' },
    { percentage: 28, label: '3.5 - 5 LPA', position: 'top-left' },
    { percentage: 46, label: '5-8 LPA', position: 'top-right' },
    { percentage: 24, label: '> 8 LPA', position: 'bottom-right' },
  ];
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % segments.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return !isMobile ? (
    <div className="relative w-full overflow-hidden py-12">
      <div className="container mx-auto px-4">
        {/* Section Title */}
        <div className="mb-6 text-center">
          <NewSectionTitle
            text=" A Bright Future Awaits"
            highlightedText="You !"
            placementPage={placementPage}
          />
        </div>

        {/* Description */}
        <div className="mb-16 text-center">
          <p className="mx-auto max-w-3xl font-proxima-nova text-[18px] font-normal leading-[150%] text-[#1F1F1F]">
            Dreamers, hustlers, and go-getters, our students come from all walks of life.
          </p>
        </div>

        {/* EllipticalSegmentChart */}
        <div className="relative mb-12 flex h-auto w-full items-center justify-center">
          <EllipticalSegmentChart segments={segments} year={year} subtitle={subtitle} />
        </div>

        {/* Right Side Content */}
        <div className="absolute bottom-0 right-0 top-1/2 mr-8 flex flex-col items-end justify-end">
          {/* Student Portrait */}
          <div className="relative -mb-32 -mr-16 h-64 w-48 overflow-visible">
            <Image
              src={personImage}
              alt="Student portrait"
              layout="fill"
              objectFit="contain"
              className="rounded-lg"
            />
          </div>

          {/* Guarantee Card */}
          <div className="-mb-32 mr-16 max-w-xs rounded-lg bg-white p-4 shadow-lg">
            <div className="font-proxima-nova-bold text-lg font-bold text-gray-800">
              Got Guaranteed Job!
            </div>
            <div className="font-proxima-nova-bold text-sm text-gray-600">
              Get ahead from crowd with AlmaBetter
            </div>
          </div>

          {/* Vector Images */}
          <div className="mr-20 flex space-x-2">
            <Image src={vectorOne} alt="vector image" className="h-16 w-auto object-contain" />
            <Image src={vectorTwo} alt="vector image" className="h-16 w-auto object-contain" />
            <Image src={vectorThree} alt="vector image" className="h-16 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="bg- flex flex-col justify-center bg-white  py-8 px-4">
      <div className="mb-10 text-center">
        <NewSectionTitle
          text=" A Bright Future Awaits"
          highlightedText="You !"
          placementPage={placementPage}
        />
      </div>
      <div className="relative top-0 left-[5%] mt-8 w-full max-w-xs py-36  ">
        <div className="absolute top-[-13px] left-[73px]">
          <div className="flex items-center gap-[10px] ">
            <span className="font-proxima-nova text-[20px] font-semibold not-italic leading-[150%] text-[#F00037]">
              28%
            </span>
            <span className="rounded-[4px] bg-[#FFDAE2] py-2 px-1 font-proxima-nova text-[13px] font-medium not-italic leading-[150%] text-[#1F1F1F]">
              3.5 - 5 LPA
            </span>
          </div>
          <Lines />
        </div>
        <div className="absolute top-[-2px] left-[265px]">
          <div className="flex items-center gap-[10px] ">
            <span className="font-proxima-nova text-[20px] font-semibold not-italic leading-[150%] text-[#F00037]">
              46%
            </span>
            <span className="whitespace-nowrap rounded-[4px] bg-[#FFDAE2] py-1 px-1 font-proxima-nova text-[13px] font-medium not-italic leading-[150%] text-[#1F1F1F]">
              5 - 8 LPA
            </span>
          </div>
          <LineTwo />
        </div>
        <div className=" absolute top-[182px] left-[111px]">
          <LineThree />
          <div className="flex items-center gap-[10px] ">
            <span className="font-proxima-nova text-[20px] font-semibold not-italic leading-[150%] text-[#F00037]">
              2%
            </span>
            <span className="whitespace-nowrap rounded-[4px] bg-[#FFDAE2] py-1 px-1 font-proxima-nova text-[13px] font-medium not-italic leading-[150%] text-[#1F1F1F]">
              {'< 3.5 LPA'}
            </span>
          </div>
        </div>
        <div className="absolute bottom-[55px] right-[15px]">
          <LineFour />
          <div className="flex items-center gap-[10px] ">
            <span className="font-proxima-nova text-[20px] font-semibold not-italic leading-[150%] text-[#F00037]">
              24%
            </span>
            <span className="whitespace-nowrap rounded-[4px] bg-[#FFDAE2] py-1 px-1 font-proxima-nova text-[13px] font-medium not-italic leading-[150%] text-[#1F1F1F]">
             { '< 8 LPA'}
            </span>
          </div>
        </div>
        <div className="absolute top-[43px] left-[50px]">
          {/* <Image src={arcOne} alt="arcOne"/> */}
          <ArcOne
            fill="#FFF"
            stroke="rgba(240, 0, 55, 0.06)"
            strokeWidth={1}
            className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
          />
        </div>
        <div className="absolute   top-[44px] left-[143px] translate-x-[2%] -translate-y-[9%] rotate-[2deg] transform ">
          {' '}
          <ArcTwo
            fill="#FFF"
            stroke="rgba(240, 0, 55, 0.06)"
            strokeWidth={1}
            className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
          />
        </div>{' '}
        <div className="absolute top-[177px]  left-[136px] -translate-x-[1%] -translate-y-[11%] rotate-[-1deg] transform ">
          {' '}
          <ArcThree
            fill="#FFF"
            stroke="rgba(240, 0, 55, 0.06)"
            strokeWidth={1}
            className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
          />
        </div>
        <div className="absolute top-[172px]  left-[93px] translate-x-[0%] translate-y-[32%] rotate-[0deg] transform ">
          {' '}
          <ArcFours
            fill="#FFF"
            stroke="rgba(240, 0, 55, 0.06)"
            strokeWidth={1}
            className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
          />
        </div>{' '}
        <div className="absolute top-[93px] left-[127px] mb-1 text-center font-proxima-nova text-[18px] font-medium text-[#F00037] opacity-80">
          {'Students Placed in'}
        </div>
        <div className="absolute top-[125px] left-[166px] text-center font-proxima-nova text-2xl font-bold text-gray-900 drop-shadow">
          2024
        </div>
        <div
          className="absolute inset-0 z-0"
          style={{
            borderRadius: '329px',
            background: '#F00037',
            filter: 'blur(262px)',
            width: '50%',
            height: '50%',
            transform: 'translateY(-50%)',
          }}
        ></div>
      </div>
    </div>
  );
}
